import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCheck, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatApiErrorDetail } from "../lib/api";

const TYPE_LABELS = {
  sla_breach: "SLA breach",
  follow_up_due: "Follow-up due",
  high_risk_assigned: "High-risk assigned",
  case_assigned: "Case assigned",
  case_reopened: "Case reopened",
};

const TYPE_STYLES = {
  sla_breach: "bg-red-100 text-red-800",
  follow_up_due: "bg-amber-100 text-amber-800",
  high_risk_assigned: "bg-red-100 text-red-800",
  case_assigned: "bg-[color:var(--surface-2)] text-brand-teal",
  case_reopened: "bg-purple-100 text-purple-800",
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 200 } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter((n) => {
    if (filter === "unread") return !n.read;
    if (filter === "read") return n.read;
    return true;
  });

  const unreadCount = items.filter((i) => !i.read).length;

  const markOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.success("All caught up");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="notifications-page" className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--page-fg)]">Notifications</h1>
          <p className="mt-1 text-sm text-[color:var(--page-fg-muted)]">
            {unreadCount > 0
              ? `You have ${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}.`
              : "You're all caught up."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-9 w-32" data-testid="notifications-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread</SelectItem>
              <SelectItem value="read">Read</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={markAll}
            disabled={unreadCount === 0}
            data-testid="notifications-mark-all-button"
            variant="outline"
          >
            <CheckCheck className="mr-1 h-4 w-4" />
            Mark all read
          </Button>
        </div>
      </div>

      <div className="brand-card overflow-hidden">
        <Table data-testid="notifications-table">
          <TableHeader>
            <TableRow className="brand-table-header border-b-brand-ivoryDark hover:bg-transparent">
              <TableHead />
              <TableHead>Type</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Received</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-sm text-[color:var(--page-fg-muted)]">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-16 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--surface-2)] text-brand-teal">
                    <Inbox className="h-5 w-5" />
                  </div>
                  <p className="mt-3 text-sm font-medium text-[color:var(--page-fg)]">
                    Nothing to show here.
                  </p>
                  <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">
                    New notifications will appear here as cases require attention.
                  </p>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filtered.map((n) => (
                <TableRow
                  key={n.id}
                  data-testid={`notification-row-${n.id}`}
                  className={`hover:bg-[color:var(--surface-2)] ${n.read ? "" : "bg-[color:var(--surface-2)]"}`}
                >
                  <TableCell className="w-6">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${
                        n.read ? "bg-gray-300" : "bg-brand-gold"
                      }`}
                    />
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        TYPE_STYLES[n.type] || "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {TYPE_LABELS[n.type] || n.type}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-[color:var(--page-fg)]">{n.title}</span>
                      <span className="max-w-xl text-xs text-[color:var(--page-fg-muted)]">{n.body}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-[color:var(--page-fg-muted)]">
                    {new Date(n.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {!n.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markOne(n.id)}
                          data-testid={`notifications-mark-read-${n.id}`}
                          className="text-[color:var(--page-fg-muted)] hover:text-brand-teal"
                        >
                          Mark read
                        </Button>
                      )}
                      {n.case_id && (
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          data-testid={`notifications-open-case-${n.id}`}
                        >
                          <Link to={`/cases/${n.case_id}`}>Open case →</Link>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
